/// <amd-module name="Test/TestPlansHubRefresh/Service/TestHubDataService" />
import { getTestClient, ITestRestClient } from "TestPlanCommon/RestClient/Test";
import { RunCreateModel, TestRun, TestSettings, ShallowReference } from "TestPlanCommon/Contracts/Test";
import * as Resources from "Test/TestPlansHubRefresh/Service/Resources";
import {
    CloneTestCaseOperationInformation,
    CloneTestCaseParams,
    CloneTestPlanOperationInformation,
    CloneTestPlanParams,
    CloneTestSuiteOperationInformation,
    CloneTestSuiteParams,
    DestinationTestPlanCloneParams,
    ExcludeFlags,
    SourceTestPlanInfo,
    SuiteEntry,
    SuiteEntryUpdateParams,
    SuiteExpand,
    SuiteTestCaseCreateUpdateParameters,
    ExportTestCaseParams,
    TestCase,
    TestEntityCount,
    TestPlan,
    TestPoint,
    TestPointUpdateParams,
    TestSuite,
    TestSuiteCreateParams,
    TestSuiteUpdateParams,
    UserFriendlyTestOutcome
} from "TestPlanCommon/Contracts/TestPlan";
import { TestArtifactWithContinuationToken } from "TestPlanCommon/Contracts/TestPlanExtended";
import { getTestPlanClient, ITestPlanRestClient } from "TestPlanCommon/RestClient/TestPlan";
import { getTestPlanExtendedClient, ITestPlanExtendedRestClient } from "TestPlanCommon/RestClient/TestPlanExtended";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { format } from "VSS/Core/Util/String";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

export interface ITestHubDataService extends IVssService {
    /**
     * Get a test plan by id
     * @param planId - ID of the test plan
     */
    getTestPlanById(planId: number): Promise<TestPlan>;

    /**
     * Gets the test suites list for given plan id
     * @param planId - ID of the test plan
     * @param expand - Include the children suites and testers details.
     * @param continuationToken - continuationToken
     * @param asTreeView - If the Suite data is to be returned in a tree structure.
     * @param projectName - Name of the project the plan is under.
     */
    getTestSuitesForPlan(
        planId: number,
        expand?: SuiteExpand,
        continuationToken?: string,
        asTreeView?: boolean,
        projectName?: string
    ): Promise<TestSuite[]>;

    /**
     * Fetches single single TestCase item from server. The returned array will contain a single element
     * @param planId Work item id of the test plan
     * @param suiteId Work item id of the test suite
     * @param testCaseIds Test case ids to fetch
     * @param returnIdentityRef True to get identity fields as IdentityRef objects, defaults to false.
     */
    getTestCases(planId: number, suiteId: number, testCaseIds: number[], returnIdentityRef?: boolean, witFields?: string): Promise<TestCase[]>;

    /**
     * Gets the test case list for the given suite id
     * @param planId Id of the test plan
     * @param suiteId Id of the suite
     * @param testIds List of test ids to fetch
     * @param configurationIds List of configuration ids to fetch
     * @param witFields Workitem fields to fetch
     * @param continuationToken Continuation token
     */
    getTestCasesForSuite(
        planId: number,
        suiteId: number,
        testIds?: string,
        configurationIds?: string,
        witFields?: string,
        continuationToken?: string,
        returnIdentityRef?: boolean,
        excludeFlags?: ExcludeFlags,
        expand?: boolean,
        isRecursive?: boolean
    ): Promise<TestArtifactWithContinuationToken<TestCase>>;

    /**
     * Gets the test points list for the given suite id
     * @param testPlanId Id of the test plan
     * @param testSuiteId Id of the test suite
     * @param testPointIds List of points ids to fetch
     * @param testCaseId Id of test case
     * @param continuationToken Continuation token
     * @param isRecursive If set to true, fetch the test points recursively for all child suites as well
     */
    getTestPointsForSuite(
        testPlanId: number,
        testSuiteId: number,
        testPointIds?: string,
        testCaseId?: string,
        continuationToken?: string,
        returnIdentityRef?: boolean,
        includePointDetails?: boolean,
        isRecursive?: boolean
    ): Promise<TestArtifactWithContinuationToken<TestPoint>>;

    /**
     * Update test point data (like outcome for e.g.)
     * @param params Test points to update
     * @param testPlanId Test plan id
     * @param testSuiteId Test suite id
     * @param includePointDetails - If set to false, will get a smaller payload containing only basic details about the test point object
     * @param returnIdentityRef - If set to true, returns the AssignedTo field in TestCaseReference as IdentityRef object.
     */
    updateTestPoints(
        params: TestPointUpdateParams[],
        testPlanId: number,
        testSuiteId: number,
        includePointDetails?: boolean,
        returnIdentityRef?: boolean
    ): Promise<TestPoint[]>;

    /**
     * Create Test Settings
     * @param planAreaPath Area Path of the plan
     */
    createTestSettings(planAreaPath: string): Promise<number>;

    /**
     * Create Test Run
     * @param testPlanId Test plan id
     * @param testPlanName Test plan name
     * @param testPlanIteration Test plan iteration
     * @param points Test points
     * @param suiteName suite Name
     * @param settingId Test setting Id
     */
    createTestRun(
        planId: number,
        planName: string,
        planIteration: string,
        points: TestPoint[],
        suiteName: string,
        settingId: number,
        selectedBuild: ShallowReference
    ): Promise<TestRun>;

    /**
     * Create Application Test Runner
     * @param testRunId Test run id
     * @param testSuiteId Test suiteId
     */
    openApplicationTestRunner(testRunId: number, suiteId: number): Promise<boolean>;

    /**
     * Add test case to the given suite id
     * @param params Create and update suite test case parameters
     * @param testPlanId Test plan id
     * @param testSuiteId Test suite id
     */
    addTestCasesToSelectedSuite(params: SuiteTestCaseCreateUpdateParameters[], testPlanId: number, testSuiteId: number): Promise<TestCase[]>;

    /**
     * Get clone information for an Op ID
     * @param opId OperationId
     */
    getCloneInformation(opID: number): Promise<CloneTestPlanOperationInformation>;

    /**
     * Copy a test plan
     * @param sourcePlanId Source plan Id for cloning
     * @param destPlanName Destination plan name
     * @param destAreatPath Destination area path
     * @param desIteration Destination iteration
     * @param isDeepClone enable deep clone
     */
    cloneTestPlan(
        sourcePlanId: number,
        destPlanName: string,
        desAreatPath: string,
        destAreatPath: string,
        isDeepClone: boolean
    ): Promise<CloneTestPlanOperationInformation>;

    /**
     * Clone a test suite
     * @param sourceTestSuiteId Source test suite id which is to be cloned
     * @param sourceProject Project which the source suite belongs to
     * @param destinationTestSuiteId Test suite id under which the cloned suite is to appear
     * @param destinationAreaPath Area path of the destination test suite
     * @param destinationIterationPath Iteration path of the destination test suite
     * @param isDeepClone enable deep clone
     */
    cloneTestSuite(
        sourceTestSuiteId: number,
        sourceProject: string,
        destinationTestSuiteId: number,
        destinationAreaPath: string,
        destinationIterationPath: string,
        isDeepClone: boolean
    ): Promise<CloneTestSuiteOperationInformation>;

    /**
     * Clone test cases
     * @param sourcePlanId Source plan Id
     * @param sourcePlanName Source Plan Name
     * @param destinationPlanId Target Plan Id
     * @param destinationPlanName Target Plan Name
     * @param sourceSuiteId Source suite id under which test cases are selected
     * @param destinationSuiteId Destination suite where test cases need to be deep cloned
     * @param sourceProjectName Source project to which source suite belongs
     * @param destinationProjectName Destination project to which destination suite belongs
     */
    cloneTestCases(
        sourcePlanId: number,
        sourcePlanName: string,
        destinationPlanId: number,
        destinationPlanName: string,
        sourceSuiteId: number,
        destinationSuiteId: number,
        sourceProjectName: string,
        destinationProjectName: string,
        testCaseIds: number[],
        includeLinks: boolean,
        includeAttachments: boolean
    ): Promise<CloneTestCaseOperationInformation>;

    /**
     * Add new test suite
     * @param params Create suite parameters
     * @param planId Test plan id
     */
    createTestSuite(params: TestSuiteCreateParams, planId: number): Promise<TestSuite>;

    /**
     * Add new requirement based suites
     * @param params Create suite parameters
     * @param planId Test plan id
     * @param parentSuiteId Suite Id of the parent under which new suites will be created
     */
    createBulkTestSuites(params: TestSuiteCreateParams[], planId: number, parentSuiteId: number): Promise<TestSuite[]>;

    /**
     * Deletes a test suite
     * @param planId Test plan id
     */
    deleteTestSuite(planId: number, suiteId: number): Promise<void>;

    /**
     * Update test suite
     * @param params Update suite parameters
     * @param planId Test plan id
     * @param suiteId Test suite id
     */
    updateTestSuite(params: TestSuiteUpdateParams, planId: number, suiteId: number): Promise<TestSuite>;

    /**
     * Reorder test suite entries in the test suite.
     * @param suiteEntries - List of SuiteEntry to reorder.
     * @param project - Project ID or project name
     * @param suiteId - Id of the parent test suite.
     */
    reorderSuiteEntries(suiteEntries: SuiteEntryUpdateParams[], suiteId: number): Promise<SuiteEntry[]>;

    /**
     * Fetch test entity count per suite
     * @param planId Test plan id
     * @param states list of test case state filter values
     * @param configurations list of test point configuration filter values for point count, null for test case count
     * @param testers list of test point tester filter values for point count, null otherwise for test case count
     * @param assignedTo list of test case assignedTo filter values
     * @param outcome Test point outcome filter value for point count, null otherwise for test case count
     */
    fetchTestEntityCount(
        planId: number,
        states: string[],
        configurations: number[],
        testers: string[],
        assignedTo: string[],
        outcome?: UserFriendlyTestOutcome,
        testEntity?: number
    ): Promise<TestEntityCount[]>;

    /**
     * Remove list of test cases from suite
     * @param planId Test plan Id
     * @param suiteId Test Suite Id
     * @param testCaseIds list of test cases to be removed from suite
     */
    removeTestCasesFromSuite(planId: number, suiteId: number, testCaseIds: string): Promise<void>;

    /**
     * Get a list of test suites in which a test case is part of
     * @param testCaseId
     */
    getSuitesByTestCaseId(testCaseId: number): Promise<TestSuite[]>;

    /**
     * Get the list of projects in the organization.
     * @param top The number of projects to be fetched
     */
    getProjects(top?: number): Promise<TeamProjectReference[]>;

    /**
     * Get the list of test plans under the specified project.
     * @param project Project ID or project name.
     * @param owner Filter for test plan by owner ID or name.
     * @param continuationToken Continuation token.
     * @param includePlanDetails Get all properties of the test plan.
     * @param filterActivePlans Get just the active plans.
     */
    getTestPlans(
        project: string,
        owner?: string,
        continuationToken?: string,
        includePlanDetails?: boolean,
        filterActivePlans?: boolean
    ): Promise<TestArtifactWithContinuationToken<TestPlan>>;

    /**
     * Get suite clone information.
     *
     * @param opID - Operation ID returned when we queue a clone operation
     * @param project - Project under which the clone operation was initiated, i.e. the project to which the source suite belongs.
     */
    getSuiteCloneInformation(opID: number, project?: string): Promise<CloneTestSuiteOperationInformation>;

    /**
     * Get testcase clone information.
     *
     * @param opID - Operation ID returned when we queue a clone operation
     * @param project - Project under which the clone operation was initiated, i.e. the project to which the source suite belongs.
     */
    getTestCaseCloneInformation(opID: number, project?: string): Promise<CloneTestCaseOperationInformation>;

    /**
     * Exports test cases to excel
     * @param planId Test plan Id
     * @param suiteId Test Suite Id
     * @param testCaseIds list of test cases to be exported
     */
    exportTestCasesToExcel(exportTestCaseParams: ExportTestCaseParams): Promise<ArrayBuffer>;
}

export class TestHubDataService extends VssService implements ITestHubDataService {
    protected pageContext: IVssPageContext;
    protected testPlanClient: ITestPlanRestClient;
    protected testPlanExtendedClient: ITestPlanExtendedRestClient;
    protected testRunClient: ITestRestClient;
    protected testSettingsXml: string =
        '<TestSettings xmlns="http://microsoft.com/schemas/VisualStudio/TeamTest/2010"><Execution xmlns="http://microsoft.com/schemas/VisualStudio/TeamTest/2010"><TestTypeSpecific /><AgentRule name="Local"><SelectionCriteria><AgentProperty name="__Test Environment" value="__Placeholder_TestEnvironmentName" /><AgentProperty name="__Machine Role" value="Local" /></SelectionCriteria><DataCollectors></DataCollectors></AgentRule></Execution></TestSettings>';

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.pageContext = pageContext;
        this.testPlanClient = getTestPlanClient(pageContext);
        this.testRunClient = getTestClient(pageContext);
        this.testPlanExtendedClient = getTestPlanExtendedClient(pageContext);
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);

        this.pageContext = pageContext;
        this.testPlanClient = getTestPlanClient(pageContext);
        this.testRunClient = getTestClient(pageContext);
        this.testPlanExtendedClient = getTestPlanExtendedClient(pageContext);
    }

    public createTestSettings(planAreaPath: string): Promise<number> {
        const settings: TestSettings = {
            areaPath: planAreaPath,
            description: Resources.TestSettingDescription,
            isPublic: false,
            machineRoles: '<roles><role name="Local" execution= "true"/></roles>',
            testSettingsContent: this.testSettingsXml,
            testSettingsName: "None",
            testSettingsId: -1
        };
        const projectName = this._getCurrentProject();
        return this.testRunClient.createTestSettings(settings, projectName);
    }

    public createTestRun(
        planId: number,
        planName: string,
        planIteration: string,
        points: TestPoint[],
        suiteName: string,
        settingId: number,
        selectedBuild: ShallowReference
    ): Promise<TestRun> {
        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined = pageService.getData();

        let owner = "";
        if (pageData && pageData.user) {
            owner = pageData.user.id;
        }

        const testRunModel: RunCreateModel = {} as RunCreateModel;
        testRunModel.plan = {
            id: planId.toString(),
            name: planName,
            url: ""
        };

        testRunModel.name = format(Resources.Title, suiteName);
        testRunModel.iteration = planIteration;
        testRunModel.owner = { id: owner };
        testRunModel.testSettings = { id: settingId.toString(), name: "None", url: "" };
        //this cannot be localized string as the value is being used in enum
        testRunModel.state = "InProgress";
        testRunModel.pointIds = points.map((point: any) => point.id);
        testRunModel.configurationIds = points.map((point: any) => point.configuration.id);
        testRunModel.build = selectedBuild;
        const projectName = this._getCurrentProject();
        return this.testRunClient.createTestRun(testRunModel, projectName);
    }

    public openApplicationTestRunner(testRunId: number, suiteId: number): Promise<boolean> {
        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined = pageService.getData();
        let isHosted: boolean;
        let path = "";

        if (pageData && pageData.user) {
            isHosted = pageData.isHosted;
            path = pageData.hostPath;
        }

        const projectName = this._getCurrentProject();
        return new Promise((resolve, reject) => {
            const legacyService = this.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
            legacyService.getLegacyPageContext().then((legacyPageContext: any) => {
                if (legacyPageContext && legacyPageContext.microsoftAjaxConfig && legacyPageContext.microsoftAjaxConfig.cultureInfo) {
                    const uri = this._getTestRunnerUri(
                        testRunId,
                        suiteId,
                        isHosted,
                        projectName,
                        path,
                        legacyPageContext.microsoftAjaxConfig.cultureInfo.name
                    );
                    window.open(uri, "_self");
                    resolve(true);
                }
            }, reject);
        });
    }

    public getTestPlanById(planId: number): Promise<TestPlan> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.getTestPlanById(projectName, planId);
    }

    public getTestSuitesForPlan(
        planId: number,
        expand?: number,
        continuationToken?: string,
        asTreeView?: boolean,
        projectName?: string
    ): Promise<TestSuite[]> {
        let project = projectName ? projectName : this._getCurrentProject();

        return this.testPlanClient.getTestSuitesForPlan(project, planId, expand, continuationToken, asTreeView);
    }

    public getTestCasesForSuite(
        planId: number,
        suiteId: number,
        testIds?: string,
        configurationIds?: string,
        witFields?: string,
        continuationToken?: string,
        returnIdentityRef?: boolean,
        excludeFlags?: ExcludeFlags,
        expand?: boolean,
        isRecursive?: boolean
    ): Promise<TestArtifactWithContinuationToken<TestCase>> {
        const projectName = this._getCurrentProject();
        return this.testPlanExtendedClient.getTestCaseListWithContinuationToken(
            projectName,
            planId,
            suiteId,
            testIds,
            configurationIds,
            witFields,
            continuationToken,
            expand,
            returnIdentityRef,
            excludeFlags,
            isRecursive
        );
    }

    public getTestCases(
        planId: number,
        suiteId: number,
        testCaseIds: number[],
        returnIdentityRef?: boolean,
        witFields?: string
    ): Promise<TestCase[]> {
        const projectName = this._getCurrentProject();

        return this.testPlanClient.getTestCase(projectName, planId, suiteId, testCaseIds.join(","), witFields, returnIdentityRef);
    }

    public getTestPointsForSuite(
        testPlanId: number,
        testSuiteId: number,
        testPointIds?: string,
        testCaseId?: string,
        continuationToken?: string,
        returnIdentityRef?: boolean,
        includePointDetails?: boolean,
        isRecursive?: boolean
    ): Promise<TestArtifactWithContinuationToken<TestPoint>> {
        const projectName = this._getCurrentProject();
        return this.testPlanExtendedClient.getPointsListWithContinuationToken(
            projectName,
            testPlanId,
            testSuiteId,
            testPointIds,
            testCaseId,
            continuationToken,
            returnIdentityRef,
            includePointDetails,
            isRecursive
        );
    }

    public updateTestPoints(
        params: TestPointUpdateParams[],
        testPlanId: number,
        testSuiteId: number,
        includePointDetails?: boolean,
        returnIdentityRef?: boolean
    ): Promise<TestPoint[]> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.updateTestPoints(params, projectName, testPlanId, testSuiteId, includePointDetails, returnIdentityRef);
    }

    public addTestCasesToSelectedSuite(params: SuiteTestCaseCreateUpdateParameters[], testPlanId: number, testSuiteId: number): Promise<TestCase[]> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.addTestCasesToSuite(params, projectName, testPlanId, testSuiteId);
    }

    public createTestSuite(params: TestSuiteCreateParams, planId: number): Promise<TestSuite> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.createTestSuite(params, projectName, planId);
    }

    public createBulkTestSuites(params: TestSuiteCreateParams[], planId: number, parentSuiteId: number): Promise<TestSuite[]> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.createBulkTestSuites(params, projectName, planId, parentSuiteId);
    }

    public deleteTestSuite(planId: number, suiteId: number): Promise<void> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.deleteTestSuite(projectName, planId, suiteId);
    }

    public updateTestSuite(params: TestSuiteUpdateParams, planId: number, suiteId: number): Promise<TestSuite> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.updateTestSuite(params, projectName, planId, suiteId);
    }

    public reorderSuiteEntries(params: SuiteEntryUpdateParams[], suiteId: number): Promise<SuiteEntry[]> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.reorderSuiteEntries(params, projectName, suiteId);
    }

    public removeTestCasesFromSuite(planId: number, suiteId: number, testCaseIds: string): Promise<void> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.removeTestCasesListFromSuite(projectName, planId, suiteId, testCaseIds);
    }

    public exportTestCasesToExcel(exportTestCaseParams: ExportTestCaseParams): Promise<ArrayBuffer> {
        const projectName = this._getCurrentProject();
        return this.testPlanClient.exportTestCases(exportTestCaseParams, projectName);
    }

    public getSuitesByTestCaseId(testCaseId: number): Promise<TestSuite[]> {
        return this.testPlanClient.getSuitesByTestCaseId(testCaseId);
    }

    public fetchTestEntityCount(
        planId: number,
        states: string[],
        configurations: number[],
        testers: string[],
        assignedTo: string[],
        outcome?: UserFriendlyTestOutcome,
        testEntity?: number
    ): Promise<TestEntityCount[]> {
        const projectName = this._getCurrentProject();
        const statesParam = states && states.length > 0 ? states.join(",") : undefined;
        const configurationsParam = configurations && configurations.length > 0 ? configurations.join(",") : undefined;
        const testersParam = testers && testers.length > 0 ? testers.join(",") : undefined;
        const assignedToParam = assignedTo && assignedTo.length > 0 ? assignedTo.join(",") : undefined;

        return this.testPlanClient.getTestEntityCountByPlanId(
            projectName,
            planId,
            statesParam,
            outcome,
            configurationsParam,
            testersParam,
            assignedToParam,
            testEntity
        );
    }

    public getCloneInformation(opId: number): Promise<CloneTestPlanOperationInformation> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            return this.testPlanClient.getCloneInformation(tfsPageData.project.id, opId);
        } else {
            throw new Error("Unable to get project info from page data, opID:" + opId);
        }
    }

    public cloneTestPlan(
        sourcePlanId: number,
        destPlanName: string,
        destAreatPath: string,
        desIteration: string,
        isDeepClone: boolean
    ): Promise<CloneTestPlanOperationInformation> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const destinationTestPlan: DestinationTestPlanCloneParams = {} as DestinationTestPlanCloneParams;
            destinationTestPlan.areaPath = destAreatPath;
            destinationTestPlan.iteration = desIteration;
            destinationTestPlan.project = tfsPageData.project.name;
            destinationTestPlan.name = destPlanName;

            const sourceTestPlanInfo: SourceTestPlanInfo = {} as SourceTestPlanInfo;
            sourceTestPlanInfo.id = sourcePlanId;
            sourceTestPlanInfo.suiteIds = [sourcePlanId + 1];

            const cloneTestPlanParam: CloneTestPlanParams = {
                cloneOptions: {
                    cloneRequirements: false,
                    copyAllSuites: true,
                    copyAncestorHierarchy: true
                },
                destinationTestPlan: destinationTestPlan,
                sourceTestPlan: sourceTestPlanInfo
            };

            return this.testPlanClient.cloneTestPlan(cloneTestPlanParam, tfsPageData.project.id, isDeepClone);
        } else {
            throw new Error("Unable to get project info from page data");
        }
    }

    public cloneTestSuite(
        sourceTestSuiteId: number,
        sourceProject: string,
        destinationTestSuiteId: number,
        destinationAreaPath: string,
        destinationIterationPath: string,
        isDeepClone: boolean
    ): Promise<CloneTestSuiteOperationInformation> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const cloneTestPlanParam: CloneTestSuiteParams = {
                cloneOptions: {
                    cloneRequirements: false,
                    copyAllSuites: true,
                    copyAncestorHierarchy: false,
                    overrideParameters: {
                        "System.AreaPath": destinationAreaPath,
                        "System.IterationPath": destinationIterationPath
                    }
                },
                destinationTestSuite: {
                    id: destinationTestSuiteId,
                    project: tfsPageData.project.name
                },
                sourceTestSuite: {
                    id: sourceTestSuiteId
                }
            };

            return this.testPlanClient.cloneTestSuite(cloneTestPlanParam, sourceProject, isDeepClone);
        } else {
            throw new Error("Unable to get project info from page data");
        }
    }

    public cloneTestCases(
        sourcePlanId: number,
        sourcePlanName: string,
        destinationPlanId: number,
        destinationPlanName: string,
        sourceSuiteId: number,
        destinationSuiteId: number,
        sourceProjectName: string,
        destinationProjectName: string,
        testCaseIds: number[],
        includeLinks: boolean,
        includeAttachments: boolean
    ): Promise<CloneTestCaseOperationInformation> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            const cloneTestCaseParams: CloneTestCaseParams = {
                cloneOptions: {
                    includeAttachments: includeAttachments,
                    includeLinks: includeLinks
                },
                sourceTestPlan: {
                    id: sourcePlanId,
                    name: sourcePlanName
                },
                sourceTestSuite: {
                    id: sourceSuiteId
                },
                destinationTestPlan: {
                    id: destinationPlanId,
                    name: destinationPlanName
                },
                destinationTestSuite: {
                    id: destinationSuiteId,
                    project: destinationProjectName
                },
                testCaseIds: testCaseIds
            };

            return this.testPlanClient.cloneTestCase(cloneTestCaseParams, sourceProjectName);
        } else {
            throw new Error("Unable to get project info from page data");
        }
    }

    private _getCurrentProject(): string {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        let projectName = "";

        if (tfsPageData && tfsPageData.project) {
            projectName = tfsPageData.project.name;
            return projectName;
        } else {
            throw new Error("Unable to fetch the current project");
        }
    }

    private _getTestRunnerUri(testRunId: number, suiteId: number, isHosted: boolean, projectName: string, path: string, locale: string): string {
        const uriProtocol = "test-runner";
        const operation: string = format(
            "run?runid={0},suiteid={1},protocolversion={2},isHosted={3},locale={4}",
            testRunId,
            suiteId,
            "1.0",
            isHosted,
            locale
        );
        const clientUrl: string = format("{0}://{1}/{2}/{3}", uriProtocol, location.origin + path, projectName, operation);
        return clientUrl;
    }

    public getProjects(top = 300): Promise<TeamProjectReference[]> {
        const coreRestClient = this.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
        return coreRestClient.getProjects(undefined, top); //top specifies the number of projects to be fetched
    }

    public getTestPlans(
        project: string,
        owner?: string,
        continuationToken?: string,
        includePlanDetails?: boolean,
        filterActivePlans?: boolean
    ): Promise<TestArtifactWithContinuationToken<TestPlan>> {
        return this.testPlanExtendedClient.getPlansListWithContinuationToken(
            project,
            owner,
            continuationToken,
            includePlanDetails,
            filterActivePlans
        );
    }

    public getSuiteCloneInformation(opID: number, project?: string): Promise<CloneTestSuiteOperationInformation> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (project) {
            return this.testPlanClient.getSuiteCloneInformation(project, opID);
        } else if (tfsPageData && tfsPageData.project) {
            return this.testPlanClient.getSuiteCloneInformation(tfsPageData.project.id, opID);
        } else {
            throw new Error("Unable to get project info from page data, opID:" + opID);
        }
    }

    public getTestCaseCloneInformation(opID: number, project?: string): Promise<CloneTestCaseOperationInformation> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (project) {
            return this.testPlanClient.getTestCaseCloneInformation(project, opID);
        } else if (tfsPageData && tfsPageData.project) {
            return this.testPlanClient.getTestCaseCloneInformation(tfsPageData.project.id, opID);
        } else {
            throw new Error("Unable to get project info from page data, opID:" + opID);
        }
    }
}

Services.add("ITestHubDataService", { serviceFactory: TestHubDataService });
